import { useState, useRef } from 'react'
import InputUser from '../../components/FormControl/input/InputUser'
import InputPassword from '../../components/FormControl/input/InputPassword'
import ButtonStartIcon from '../../components/button/ButtonStartIcon'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { handlerErrorV2 } from '../../utils/errors'
import { checkEmail, signIn } from '../../services/auth/auth.service'
import { validate } from '../../utils'
import isEmpty from 'lodash/isEmpty'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { permissionsMenu } from '../../store/actions/menuActions'
import SessionHelper from '../../helper/session.helper'

const STEP_EMAIL = 'email'
const STEP_PASSWORD = 'password'
const STEP_ACTIVATION_SENT = 'activation-sent'

const Form = () => {
  const [form, setForm] = useState({ login: '', password: '' })
  const [errors, setInputErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState(STEP_EMAIL)
  const licenses = useSelector((state) => state?.tenant?.license)
  const featureFlag = useSelector((state) => state?.tenant?.featureFlag || {})
  const redirectTo = localStorage.getItem('redirect') || '/dashboard'

  const emailRef = useRef(null)
  const passwordRef = useRef(null)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const onChange = (name, value) => {
    setForm({ ...form, [name]: value })
    setInputErrors({ ...errors, [name]: null })
  }

  const onSubmitEmail = async (e) => {
    e.preventDefault()
    const fieldRules = { login: 'required|email' }
    const validationErrors = validate(fieldRules, form)
    if (!isEmpty(validationErrors)) {
      setInputErrors(validationErrors)
      return
    }

    try {
      setLoading(true)
      const result = await checkEmail({ login: form.login })
      if (!result.data && result.error) {
        setInputErrors({ message: result.error.message || 'Something went wrong. Please try again.' })
        emailRef.current?.focus()
        return
      }

      const { exists, activated } = result.data
      if (!exists) {
        setInputErrors({ message: 'User not found. Please check your email address.' })
        emailRef.current?.focus()
      } else if (activated) {
        setStep(STEP_PASSWORD)
        setInputErrors({})
      } else {
        setStep(STEP_ACTIVATION_SENT)
        setInputErrors({})
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      setInputErrors({ message })
      emailRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  const onSubmitPassword = async (e) => {
    e.preventDefault()
    const fieldRules = { password: 'required' }
    const validationErrors = validate(fieldRules, form)
    if (!isEmpty(validationErrors)) {
      setInputErrors(validationErrors)
      passwordRef.current?.focus()
      return
    }

    try {
      setLoading(true)
      const result = await signIn(form)
      if (!result.data && result.error) {
        setInputErrors({ message: result.error.message || 'Something went wrong. Please try again.' })
        passwordRef.current?.focus()
      } else {
        SessionHelper.saveSession(result.data)
        const charts = await dispatch(permissionsMenu(result.data, null, licenses, featureFlag)).catch(
          () => []
        )
        const userRoles = result.data.roles || []
        const userRoleIds = userRoles.map((r) => String(r._id || r))
        const isParent = userRoles.some((r) => (r.name || r.code || '').toLowerCase().includes('parent'))
        const defaultChart =
          !isParent &&
          charts?.find((c) => c.defaultForRole?.some((roleId) => userRoleIds.includes(String(roleId))))
        const target = defaultChart ? `/dashboard/analytics/${defaultChart.code}` : redirectTo
        navigate(target)
        localStorage.removeItem('redirect')
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      setInputErrors({ message })
      passwordRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  const onKeyPressEmail = (e) => {
    if (e.key === 'Enter') onSubmitEmail(e)
  }

  const onKeyPressPassword = (e) => {
    if (e.key === 'Enter') onSubmitPassword(e)
  }

  const onBack = () => {
    setStep(STEP_EMAIL)
    setInputErrors({})
    setForm({ ...form, password: '' })
  }

  if (step === STEP_ACTIVATION_SENT) {
    return (
      <Grid item xs={12} container alignItems='center' className='m-0' spacing={2}>
        <Grid item xs={12}>
          <Alert severity='info'>
            We sent you an email to activate your account. Please check your inbox and follow the instructions
            to get started.
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <Button variant='text' onClick={onBack}>
            Back to login
          </Button>
        </Grid>
      </Grid>
    )
  }

  if (step === STEP_PASSWORD) {
    return (
      <Grid item xs={12} container alignItems='center' className='m-0' spacing={2}>
        <Grid item xs={12}>
          <Typography variant='body2' color='text.secondary'>
            {form.login}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <InputPassword
            id='login-password'
            variant='outlined'
            label='Password'
            error={errors?.password}
            helperText={errors?.password}
            onChange={(e) => onChange('password', e.target.value)}
            onKeyPress={onKeyPressPassword}
            disabled={loading}
            autoFocus
            inputRef={passwordRef}
          />
        </Grid>
        {errors.message && (
          <Grid item xs={12}>
            <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
              {errors.message}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <ButtonStartIcon
            size='medium'
            loading={loading}
            className='login-button'
            onClick={onSubmitPassword}
            startIcon='sign-in'
          >
            sign in
          </ButtonStartIcon>
        </Grid>
        <Grid item xs={12}>
          <Button variant='text' onClick={onBack} disabled={loading}>
            Back
          </Button>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid item xs={12} container alignItems='center' className='m-0' spacing={2}>
      <Grid item xs={12}>
        <InputUser
          disabled={loading}
          error={errors?.login}
          helperText={errors?.login}
          onChange={(e) => onChange('login', e.target.value)}
          onKeyPress={onKeyPressEmail}
          autoFocus
          inputRef={emailRef}
        />
      </Grid>
      {errors.message && (
        <Grid item xs={12}>
          <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
            {errors.message}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <ButtonStartIcon
          size='medium'
          loading={loading}
          className='login-button'
          onClick={onSubmitEmail}
          startIcon='arrow-right'
        >
          continue
        </ButtonStartIcon>
      </Grid>
    </Grid>
  )
}

export default Form
